import React, { useState } from 'react';
import MediaPickerModal from './MediaPickerModal';
import FocalPad from './FocalPad';
import {
    getBackgroundLayers, newLayer, MAX_BG_LAYERS, BG_SIZES,
} from '../../Portal/backgroundLayers';

const SIZE_LABELS = { cover: 'Cover (fill the area)', contain: 'Contain (fit inside)', auto: 'Original size' };

// 3x3 alignment presets: [x%, y%, label]
const ALIGN_POINTS = [
    [0, 0, 'top left'], [50, 0, 'top'], [100, 0, 'top right'],
    [0, 50, 'left'], [50, 50, 'center'], [100, 50, 'right'],
    [0, 100, 'bottom left'], [50, 100, 'bottom'], [100, 100, 'bottom right'],
];

/**
 * Inspector editor for a Background node's stacked images. Every change goes
 * out through onChange(nextLayers) as ONE update, so each add / reorder /
 * alignment is a single undo step.
 */
export default function BackgroundLayersEditor({ node, tenantId, onChange }) {
    const layers = getBackgroundLayers(node);
    // 'add' while choosing a new image, a layer index while replacing one, else null.
    const [picker, setPicker] = useState(null);

    const update = (index, patch) => onChange(layers.map((l, i) => (i === index ? { ...l, ...patch } : l)));
    const remove = (index) => onChange(layers.filter((_, i) => i !== index));
    const move = (index, direction) => {
        const target = index + direction;
        if (target < 0 || target >= layers.length) return;
        const next = [...layers];
        [next[index], next[target]] = [next[target], next[index]];
        onChange(next);
    };

    function handlePick(url) {
        if (picker === 'add') onChange([...layers, newLayer(url)]);
        else if (typeof picker === 'number') update(picker, { src: url });
    }

    return (
        <div className="lb-bg-layers">
            <label>Background images</label>
            <p className="lb-bg-hint">
                {layers.length > 1
                    ? 'Layer 1 is in front. Drag inside a picture to align it, or use the grid.'
                    : 'Add one or more pictures. Drag inside a picture to align it, or use the grid.'}
            </p>

            {layers.map((layer, i) => (
                <div className="lb-bg-layer" key={i}>
                    <div className="lb-bg-layer-head">
                        <strong>{i === 0 ? 'Layer 1 (front)' : `Layer ${i + 1}`}</strong>
                        <span className="lb-bg-layer-actions">
                            <button type="button" title="Move forward" aria-label="Move layer forward" disabled={i === 0} onClick={() => move(i, -1)}>
                                <i className="fas fa-arrow-up"></i>
                            </button>
                            <button type="button" title="Move back" aria-label="Move layer back" disabled={i === layers.length - 1} onClick={() => move(i, 1)}>
                                <i className="fas fa-arrow-down"></i>
                            </button>
                            <button type="button" title="Replace image" aria-label="Replace image" onClick={() => setPicker(i)}>
                                <i className="fas fa-image"></i>
                            </button>
                            <button type="button" title="Remove image" aria-label="Remove image" className="danger" onClick={() => remove(i)}>
                                <i className="fas fa-trash"></i>
                            </button>
                        </span>
                    </div>

                    <FocalPad src={layer.src} x={layer.x} y={layer.y} size={layer.size} repeat={layer.repeat} onCommit={(x, y) => update(i, { x, y })} />

                    <div className="lb-bg-align-row">
                        <div className="lb-bg-align-grid" role="group" aria-label="Alignment">
                            {ALIGN_POINTS.map(([x, y, label]) => (
                                <button
                                    key={label}
                                    type="button"
                                    title={`Align ${label}`}
                                    aria-label={`Align ${label}`}
                                    className={layer.x === x && layer.y === y ? 'active' : ''}
                                    onClick={() => update(i, { x, y })}
                                />
                            ))}
                        </div>
                        <div className="lb-bg-layer-options">
                            <select className="form-control" aria-label="Image size" value={layer.size} onChange={(e) => update(i, { size: e.target.value })}>
                                {BG_SIZES.map((s) => <option key={s} value={s}>{SIZE_LABELS[s]}</option>)}
                            </select>
                            <label className="lb-bg-repeat">
                                <input type="checkbox" checked={!!layer.repeat} onChange={(e) => update(i, { repeat: e.target.checked })} />
                                Repeat (tile)
                            </label>
                            <span className="lb-bg-coords">{layer.x}% · {layer.y}%</span>
                        </div>
                    </div>
                </div>
            ))}

            <button
                type="button"
                className="btn-secondary-modern lb-bg-add"
                disabled={layers.length >= MAX_BG_LAYERS}
                onClick={() => setPicker('add')}
            >
                <i className="fas fa-plus"></i> Add image{layers.length >= MAX_BG_LAYERS ? ` (max ${MAX_BG_LAYERS})` : ''}
            </button>

            <MediaPickerModal
                open={picker !== null}
                mediaType="image"
                tenantId={tenantId}
                onSelect={handlePick}
                onClose={() => setPicker(null)}
            />
        </div>
    );
}
